import { useEffect, memo } from 'react';
import {
  UserMessage,
  Heading,
  FontWeight,
  LiliButton,
  LiliButtonType,
} from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { CustomRadioButton } from '@Components/inputs/RadioButton/CustomRadioButton';
import { useNameTooLongLogic } from './useNameTooLongLogic';

export const NameSuggestionModal = memo(({ isShown }: { isShown: boolean }) => {
  const { t } = useTranslation();

  const {
    suggestions,
    fetchSuggestions,
    isFetchingSuggestions,
    selectedName,
    setSelectedName,
    reset,
    handleContinue,
    isUpdatingName,
    closeModal,
  } = useNameTooLongLogic();

  useEffect(() => {
    if (isShown) {
      fetchSuggestions();
    } else if (!isShown && suggestions) {
      reset();
    }
  }, [isShown]);

  if (!isShown) {
    return null;
  }

  return (
    <UserMessage
      id='name-suggestion-modal'
      onClick={closeModal}
      contentWrapperClassName='w-fit md:w-fit px-[16px]'>
      <div className='w-full sm:w-[430px] bg-surface-primary-bw-00 rounded-[12px] p-[24px]'>
        {/* Header */}
        <div className='mb-[24px]'>
          <Heading.H5 fontWeight={FontWeight.SEMIBOLD} className='text-text-main-primary mb-[8px]'>
            {t('pages.nameSuggestionModal.title')}
          </Heading.H5>

          <Heading.H9 fontWeight={FontWeight.REGULAR} className='text-text-main-secondary'>
            {t('pages.nameSuggestionModal.subtitle')}
          </Heading.H9>
        </div>

        {/* Radio Button Options */}
        <div className='mb-[24px] space-y-[16px]'>
          {suggestions &&
            suggestions.map((name: string, index: number) => (
              <div
                onClick={() => setSelectedName(name)}
                key={index}
                className={`flex items-center justify-between p-[16px] border border-outline-primary-bw-04 rounded-[12px] hover:border-surface-brand-action-03 transition-colors ${
                  selectedName === name ? 'border-surface-brand-action-03' : ''
                }`}>
                <Heading.H9 fontWeight={FontWeight.REGULAR} className='text-text-main-primary'>
                  {name}
                </Heading.H9>

                <CustomRadioButton
                  checked={selectedName === name}
                  onChange={(value) => setSelectedName(value)}
                  name='name-selection'
                  value={name}
                  label=''
                  inputId={`name-option-${index}`}
                />
              </div>
            ))}
          {isFetchingSuggestions &&
            [...Array(3)].map((_, index) => (
              <div
                key={`shimmer-${index}`}
                className='flex items-center justify-between p-[16px] border border-outline-primary-bw-04 rounded-[12px] animate-pulse'>
                {/* Shimmer text placeholder */}
                <div className='h-[20px] bg-surface-primary-bw-04 rounded w-[120px]'></div>

                {/* Shimmer radio button placeholder */}
                <div className='w-[18px] h-[18px] bg-surface-primary-bw-04 rounded-full'></div>
              </div>
            ))}
        </div>

        {/* Continue Button */}
        <div className='w-full'>
          <LiliButton
            type={LiliButtonType.PRIMARY}
            onClick={handleContinue}
            className='w-full h-[48px]'
            disabled={!selectedName || isUpdatingName}
            isLoading={isUpdatingName}
            title={t('pages.nameSuggestionModal.continueButton')}
            id='name-suggestion-continue-btn'
          />
        </div>
      </div>
    </UserMessage>
  );
});

NameSuggestionModal.displayName = 'NameSuggestionModal';
